import { apiService } from '../services/api';

interface HandlersProps {
  id: string;
  saveWithData: (data: any) => Promise<boolean>;
  saveDisplay: (data: any) => Promise<boolean>;
  updateField: (field: string, value: any) => void;
  reload: () => Promise<void>;
  uploadImage: (file: File) => Promise<{ success: boolean; url?: string }>;
}

export function useSpaceEditHandlers({
  id,
  saveWithData,
  saveDisplay,
  updateField,
  reload,
  uploadImage,
}: HandlersProps) {
  const DEDICATED_API_KEYS = new Set([
    'capacityBrackets',
    'requiredDocs',
    'roomLayouts',
    'equipments',
    'cateringPrices',
  ]);

  const DISPLAY_KEYS = new Set(['tarifGroups', 'services', 'processSteps', 'notes', 'infoBadges']);

  async function handleStepSave(data: any, dirtyKeys: Set<string>) {
    console.log("⚡ [WIZARD] Sauvegarde d'étape — clés modifiées:", Array.from(dirtyKeys));

    if (dirtyKeys.size === 0) return;

    try {
      const scalarPayload: Record<string, any> = {};
      const displayPayload: Record<string, any> = {};
      let hasEventTypes = false;

      for (const key of dirtyKeys) {
        if (DEDICATED_API_KEYS.has(key)) continue;
        if (DISPLAY_KEYS.has(key)) {
          displayPayload[key] = data[key];
        } else if (key === 'formSections') {
          // Skip
        } else if (key === 'eventTypes') {
          hasEventTypes = true;
        } else {
          scalarPayload[key] = data[key];
        }
      }

      const scalarAndEvents: Record<string, any> = { ...scalarPayload };
      if (hasEventTypes) scalarAndEvents.eventTypes = data.eventTypes;

      if (Object.keys(scalarAndEvents).length > 0) {
        await saveWithData(scalarAndEvents);
      }

      if (Object.keys(displayPayload).length > 0) {
        await saveDisplay(displayPayload);
      }

      if (dirtyKeys.has('capacityBrackets')) {
        await apiService.updateCapacityBrackets(id, data.capacityBrackets);
        updateField('capacityBrackets', data.capacityBrackets);
      }

      if (dirtyKeys.has('requiredDocs')) {
        await apiService.updateRequiredDocuments(id, data.requiredDocs);
        updateField('requiredDocs', data.requiredDocs);
      }

      if (dirtyKeys.has('roomLayouts') || dirtyKeys.has('roomLayoutsEnabled')) {
        const layoutsToSave = data.roomLayoutsEnabled ? data.roomLayouts || [] : [];
        const resolvedLayouts = await Promise.all(
          layoutsToSave.map(async (layout: any) => {
            if (layout.pendingFile) {
              const result = await uploadImage(layout.pendingFile);
              if (result.success && result.url) {
                const { pendingFile, previewUrl, ...rest } = layout;
                return { ...rest, imageUrl: result.url };
              }
            }
            const { pendingFile, previewUrl, ...rest } = layout;
            return rest;
          }),
        );
        await apiService.updateRoomLayouts(id, resolvedLayouts);
        updateField('roomLayouts', resolvedLayouts);
        if (dirtyKeys.has('roomLayoutsEnabled')) updateField('roomLayoutsEnabled', data.roomLayoutsEnabled);
      }

      if (dirtyKeys.has('equipments') || dirtyKeys.has('equipmentsEnabled')) {
        const eqToSave = data.equipmentsEnabled ? data.equipments || [] : [];
        await apiService.updateEquipments(id, eqToSave);
        updateField('equipments', eqToSave);
        if (dirtyKeys.has('equipmentsEnabled')) updateField('equipmentsEnabled', data.equipmentsEnabled);
      }

      if (dirtyKeys.has('cateringPrices') || dirtyKeys.has('cateringEnabled')) {
        const catToSave = data.cateringEnabled ? data.cateringPrices || [] : [];
        await apiService.updateCateringPrices(id, catToSave);
        updateField('cateringPrices', catToSave);
        if (dirtyKeys.has('cateringEnabled')) updateField('cateringEnabled', data.cateringEnabled);
      }

      if (dirtyKeys.has('imageUrl') && data.imageUrl?.startsWith('data:image')) {
        const response = await fetch(data.imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'space-image.jpg', { type: blob.type });
        const result = await uploadImage(file);
        if (result.success && result.url) {
          await apiService.updateSpaceScalars(id, { imageUrl: result.url });
        }
      }
    } catch (error: any) {
      // Extraire le message d'erreur du backend (Axios encapsule la réponse)
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur inconnue lors de la sauvegarde';

      const details = error?.response?.data?.details || '';
      const fullMessage = details ? `${backendMessage} (${details})` : backendMessage;

      console.error('❌ [WIZARD] Erreur handleStepSave:', fullMessage, error);
      throw new Error(fullMessage);
    }
  }

  async function handleFinish(data: any) {
    try {
      let imageUrl = data.imageUrl;
      if (imageUrl?.startsWith('data:image')) {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'space-image.jpg', { type: blob.type });
        const result = await uploadImage(file);
        if (result.success && result.url) imageUrl = result.url;
        else imageUrl = undefined;
      }

      const SCALAR_FIELDS = [
        'name',
        'slug',
        'category',
        'description',
        'shortDesc',
        'workflow',
        'colorPrimary',
        'colorAccent',
        'iconName',
        'bannerUrl',
        'isActive',
        'isPublic',
        'order',
        'subtitle',
        'badge',
        'badgeColor',
        'gradient',
        'ctaLabel',
        'priceFromLabel',
        'priceFromDetail',
        'defaultDuration',
        'metaTitle',
        'metaDescription',
        'importantNotes',
        'formSchema',
        'validationRules',
        'requireCompanyName',
        'companyFieldLabel',
        'requireCompanyInfo',
        'requireCompanyPhone',
        'requireCompanyEmail',
        'requireCompanyWebsite',
        'companyPhoneLabel',
        'companyEmailLabel',
        'companyWebsiteLabel',
        'requireBookingReason',
        'bookingReasonLabel',
        'eventTypesEnabled',
        'requireEventObjectives',
        'eventObjectivesLabel',
        'allowMultiDay',
        'allowHalfDay',
        'allowFullDay',
        'allowMonth',
        'fullDayHours',
        'halfDayHours',
        'quoteDelayHours',
        'urgentQuotePrice',
        'urgentQuoteDelayHours',
        'quoteRules',
        'equipmentsEnabled',
        'roomLayoutsEnabled',
        'cateringEnabled',
      ];

      const scalars: Record<string, any> = {};
      for (const key of SCALAR_FIELDS) {
        if (data[key] !== undefined) scalars[key] = data[key];
      }
      if (imageUrl !== undefined && !imageUrl?.startsWith('data:')) {
        scalars.imageUrl = imageUrl;
      }

      const commitPayload: Record<string, any> = {};
      if (Object.keys(scalars).length > 0) commitPayload.scalars = scalars;
      if (Array.isArray(data.eventTypes)) commitPayload.eventTypes = data.eventTypes;

      const display: Record<string, any> = {};
      if (data.tarifGroups) display.tarifGroups = data.tarifGroups;
      if (data.services) display.services = data.services;
      if (data.processSteps) display.processSteps = data.processSteps;
      if (data.notes) display.notes = data.notes;
      if (data.infoBadges) display.infoBadges = data.infoBadges;
      if (Object.keys(display).length > 0) commitPayload.display = display;

      await apiService.commitSpace(id, commitPayload);

      // Dedicated APIs
      if (Array.isArray(data.capacityBrackets)) {
        await apiService.updateCapacityBrackets(id, data.capacityBrackets);
      }
      if (Array.isArray(data.requiredDocs)) {
        await apiService.updateRequiredDocuments(id, data.requiredDocs);
      }
      if (data.roomLayoutsEnabled !== undefined || Array.isArray(data.roomLayouts)) {
        const layoutsToSave = data.roomLayoutsEnabled ? data.roomLayouts || [] : [];
        const resolvedLayouts = await Promise.all(
          layoutsToSave.map(async (layout: any) => {
            if (layout.pendingFile) {
              const result = await uploadImage(layout.pendingFile);
              if (result.success && result.url) {
                const { pendingFile, previewUrl, ...rest } = layout;
                return { ...rest, imageUrl: result.url };
              }
            }
            const { pendingFile, previewUrl, ...rest } = layout;
            return rest;
          }),
        );
        await apiService.updateRoomLayouts(id, resolvedLayouts);
      }
      if (data.equipmentsEnabled !== undefined || Array.isArray(data.equipments)) {
        const eqToSave = data.equipmentsEnabled ? data.equipments || [] : [];
        await apiService.updateEquipments(id, eqToSave);
      }
      if (data.cateringEnabled !== undefined || Array.isArray(data.cateringPrices)) {
        const catToSave = data.cateringEnabled ? data.cateringPrices || [] : [];
        await apiService.updateCateringPrices(id, catToSave);
      }

      await reload();
    } catch (error: any) {
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Erreur inconnue lors de la finalisation';

      const details = error?.response?.data?.details || '';
      const fullMessage = details ? `${backendMessage} (${details})` : backendMessage;

      console.error('❌ [FINISH] Erreur handleFinish:', fullMessage, error);
      throw new Error(fullMessage);
    }
  }

  return { handleStepSave, handleFinish };
}
